import React from 'react'
import { Link, useLocation } from 'react-router'

import AssessmentTwoNotStartedButtons from '@/components/layouts/RqCertifiedLayout/components/RqCertifiedActionButtons/AssessmentTwoNotStartedButtons'
import ContinueApplicationButtons from '@/components/layouts/RqCertifiedLayout/components/RqCertifiedActionButtons/ContinueApplicationButtons'
import PendingPaymentConfirmationButtons from '@/components/layouts/RqCertifiedLayout/components/RqCertifiedActionButtons/PendingPaymentConfirmationButtons'
import StartApplicationButtons from '@/components/layouts/RqCertifiedLayout/components/RqCertifiedActionButtons/StartApplicationButtons'
import { Button } from '@/components/ui/button'
import useAppSelector from '@/hooks/useAppSelector'
import { useRqCertifiedAssessmentOrFail } from '@/hooks/useRqCertifiedAssessmentQuery'
import { RQ_CERTIFIED_ASSESSMENT_STATUS_TYPE } from '@/lib/constants'
import { ROUTES } from '@/lib/routes'
import { selectCurrentCompanyOrFail } from '@/redux/slices/session'

interface Props {
  children?: React.ReactNode
}

const RqCertifiedActionButtons: React.FC<Props> = ({ children }) => {
  const { pathname } = useLocation()
  const currentCompany = useAppSelector(selectCurrentCompanyOrFail)
  const assessment = useRqCertifiedAssessmentOrFail()

  const isYetToStartApplication =
    assessment.isAtStageOne &&
    assessment.currentStatus === RQ_CERTIFIED_ASSESSMENT_STATUS_TYPE.notStarted

  const isPendingPaymentConfirmation =
    assessment.currentStatus ===
    RQ_CERTIFIED_ASSESSMENT_STATUS_TYPE.pendingPaymentConfirmation

  const canContinueApplication =
    assessment.currentStatus === RQ_CERTIFIED_ASSESSMENT_STATUS_TYPE.inProgress

  const hasJustPassedAssessmentOne =
    assessment.assessmentOneStatus ===
      RQ_CERTIFIED_ASSESSMENT_STATUS_TYPE.passed &&
    assessment.assessmentTwoStatus ===
      RQ_CERTIFIED_ASSESSMENT_STATUS_TYPE.notStarted

  function buildButtons(): React.ReactNode {
    if (!currentCompany.isRqRated && isYetToStartApplication) {
      return <StartApplicationButtons />
    }

    if (isPendingPaymentConfirmation) {
      return <PendingPaymentConfirmationButtons />
    }

    if (canContinueApplication) {
      return <ContinueApplicationButtons />
    }

    if (hasJustPassedAssessmentOne) {
      return <AssessmentTwoNotStartedButtons />
    }

    return null
  }

  const buttons = buildButtons()

  return (
    <>
      <section
        className="mt-6 flex flex-col items-center gap-2 md:flex-row
          md:justify-end md:gap-4"
      >
        {children}

        {buttons}

        <Button asChild variant="outline-primary" className="w-full sm:w-auto">
          <Link
            to={ROUTES.help({
              searchParams: {
                backLabel: 'Back to RQ certified',
                backUrl: pathname,
              },
            })}
          >
            Discuss it with us
          </Link>
        </Button>
      </section>
    </>
  )
}

export default RqCertifiedActionButtons
